import React, { useState, useEffect } from 'react';
import {
  User, Phone, MapPin, Save, Loader2, CheckCircle, ShieldCheck,
  Wifi, SquareParking, Accessibility, Video,
} from 'lucide-react';
import { auth } from '../config/firebase';
import { usuarioService } from '../services/api';
import PaywallCard from '../components/PaywallCard';

// Formata progressivamente como (85) 98888-7766 (celular) ou (85) 8888-7766 (fixo) conforme digita.
const formatarTelefone = (valor) => {
  const digitos = valor.replace(/\D/g, '').slice(0, 11);
  if (digitos.length <= 2) return digitos;
  if (digitos.length <= 6) return `(${digitos.slice(0, 2)}) ${digitos.slice(2)}`;
  if (digitos.length <= 10) return `(${digitos.slice(0, 2)}) ${digitos.slice(2, 6)}-${digitos.slice(6)}`;
  return `(${digitos.slice(0, 2)}) ${digitos.slice(2, 7)}-${digitos.slice(7)}`;
};

const DIFERENCIAIS_DISPONIVEIS = [
  { chave: 'wifi', label: 'Wi-Fi', icone: Wifi },
  { chave: 'estacionamento', label: 'Estacionamento', icone: SquareParking },
  { chave: 'acessibilidade', label: 'Acessibilidade', icone: Accessibility },
  { chave: 'atendimento_online', label: 'Atendimento Online', icone: Video },
];

// Mantém slug e formas_pagamento carregados (mas não editáveis aqui) só pra reenviar sem
// alterar — a edição do link público vive na aba "Página Pública" e a de pagamento na aba
// "Formas de Pagamento", mas o mesmo endpoint de salvar perfil exige o valor atual de todos os
// campos pra não sobrescrever nada com vazio.
const DadosEstabelecimentoTab = ({ temAcesso }) => {
  const isPremium = temAcesso('diferenciais_cores');
  const [nome, setNome] = useState('');
  const [telefone, setTelefone] = useState('');
  const [endereco, setEndereco] = useState('');
  const [slug, setSlug] = useState('');
  const [formasPagamento, setFormasPagamento] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  const [diferenciais, setDiferenciais] = useState([]);

  useEffect(() => {
    const carregarDados = async () => {
      const user = auth.currentUser;
      if (!user) return;
      try {
        const resPerfil = await usuarioService.obterPerfil(user.uid);
        setNome(resPerfil.data.nome || '');
        const telefoneDigitos = (resPerfil.data.telefone || '').replace(/\D/g, '');
        const telefoneLocal = telefoneDigitos.length > 11 && telefoneDigitos.startsWith('55')
          ? telefoneDigitos.slice(2)
          : telefoneDigitos;
        setTelefone(formatarTelefone(telefoneLocal));
        setEndereco(resPerfil.data.endereco || '');
        setSlug(resPerfil.data.slug || '');
        setDiferenciais(resPerfil.data.diferenciais || []);
        setFormasPagamento(resPerfil.data.formas_pagamento || null);
      } catch (error) {
        console.error("Erro ao carregar perfil:", error);
      } finally {
        setLoading(false);
      }
    };
    carregarDados();
  }, []);

  const alternarDiferencial = (chave) => {
    setDiferenciais(prev => prev.includes(chave) ? prev.filter(d => d !== chave) : [...prev, chave]);
  };

  const handleSalvar = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSuccess(false);

    let telefoneLimpo = telefone.replace(/\D/g, '');
    if (telefoneLimpo.length === 11 || telefoneLimpo.length === 10) {
      telefoneLimpo = `55${telefoneLimpo}`;
    }

    try {
      const uid = auth.currentUser.uid;
      await usuarioService.atualizarPerfil(uid, { nome, telefone: telefoneLimpo, slug, endereco, diferenciais, formas_pagamento: formasPagamento });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error("Erro ao atualizar perfil:", error);
      alert(error.response?.data?.erro || "Erro ao salvar as alterações do perfil.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-14 text-center text-slate-500">
        <Loader2 className="animate-spin text-[#1883FF] mb-4" size={40} />
        <p className="text-[10px] font-black uppercase tracking-[0.2em]">Carregando Perfil...</p>
      </div>
    );
  }

  return (
    <div className="w-full max-w-[620px] animate-fade-in-up">
      <form onSubmit={handleSalvar} className="flex flex-col gap-6">

        {/* DADOS DO ESTABELECIMENTO */}
        <div className="bg-white rounded-[28px] border border-slate-100 shadow-sm p-6">
          <h3 className="text-xs font-black uppercase text-[#1e293b] tracking-wide mb-5">Dados do Estabelecimento</h3>

          <div className="flex flex-col gap-5">
            <div className="flex flex-col gap-2 text-left">
              <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider ml-1">Nome do Estabelecimento</label>
              <div className="flex items-center gap-3 bg-[#F8F7FF] border-2 border-transparent rounded-2xl px-4 py-1 transition-all duration-300 focus-within:bg-white focus-within:border-[#6D28D9]/30 focus-within:ring-4 focus-within:ring-[#6D28D9]/10 group">
                <User size={18} className="text-slate-400 transition-colors group-focus-within:text-[#6D28D9]" />
                <input
                  type="text"
                  className="w-full py-3.5 border-none bg-transparent outline-none text-sm font-bold text-[#1e293b] placeholder-slate-300"
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  placeholder="Ex: Dra. Ana / Clínica Estética Bella"
                  required
                />
              </div>
            </div>

            <div className="flex flex-col gap-2 text-left">
              <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider ml-1">Local (endereço)</label>
              <div className="flex items-center gap-3 bg-[#F8F7FF] border-2 border-transparent rounded-2xl px-4 py-1 transition-all duration-300 focus-within:bg-white focus-within:border-[#6D28D9]/30 focus-within:ring-4 focus-within:ring-[#6D28D9]/10 group">
                <MapPin size={18} className="text-slate-400 transition-colors group-focus-within:text-[#6D28D9]" />
                <input
                  type="text"
                  className="w-full py-3.5 border-none bg-transparent outline-none text-sm font-bold text-[#1e293b] placeholder-slate-300"
                  value={endereco}
                  onChange={(e) => setEndereco(e.target.value)}
                  placeholder="Ex: Rua das Flores, 123 - Centro"
                />
              </div>
            </div>

            <div className="flex flex-col gap-2 text-left">
              <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider ml-1">WhatsApp para Notificações</label>
              <div className="flex items-center gap-3 bg-[#F8F7FF] border-2 border-transparent rounded-2xl px-4 py-1 transition-all duration-300 focus-within:bg-white focus-within:border-[#6D28D9]/30 focus-within:ring-4 focus-within:ring-[#6D28D9]/10 group">
                <Phone size={18} className="text-slate-400 transition-colors group-focus-within:text-[#6D28D9]" />
                <input
                  type="text"
                  className="w-full py-3.5 border-none bg-transparent outline-none text-sm font-bold text-[#1e293b] placeholder-slate-300"
                  value={telefone}
                  onChange={(e) => setTelefone(formatarTelefone(e.target.value))}
                  placeholder="(85) 98888-7766"
                  inputMode="numeric"
                  required
                />
              </div>
              <p className="text-[10px] text-slate-400 font-semibold ml-1">
                ⚠️ Certifique-se de incluir o DDD correto para evitar falhas no envio dos lembretes.
              </p>
            </div>
          </div>
        </div>

        {/* DIFERENCIAIS DO ESTABELECIMENTO */}
        {isPremium ? (
          <div className="bg-white rounded-[28px] border border-slate-100 shadow-sm p-6">
            <h3 className="text-xs font-black uppercase text-[#1e293b] tracking-wide mb-1">Diferenciais</h3>
            <p className="text-[10px] text-slate-400 font-semibold mb-5">Aparecem como ícones na sua página pública de agendamento.</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {DIFERENCIAIS_DISPONIVEIS.map(({ chave, label, icone: Icone }) => {
                const ativo = diferenciais.includes(chave);
                return (
                  <button
                    key={chave}
                    type="button"
                    onClick={() => alternarDiferencial(chave)}
                    className={`flex flex-col items-center justify-center gap-2 py-4 rounded-2xl border-2 cursor-pointer transition-all ${ativo ? 'bg-[#F5F3FF] border-[#6D28D9] text-[#6D28D9]' : 'bg-[#F8F7FF] border-transparent text-slate-400 hover:border-purple-100'}`}
                  >
                    <Icone size={22} />
                    <span className="text-[9px] font-black uppercase tracking-wide text-center leading-tight">{label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          <PaywallCard
            titulo="Diferenciais"
            descricao="Destaque comodidades do seu espaço na sua página pública."
            plano="Comum"
            className="min-h-0"
          />
        )}

        <button
          type="submit"
          disabled={saving}
          className={`w-full py-4 border-none rounded-2xl text-white font-black text-xs uppercase tracking-wider flex items-center justify-center gap-2.5 transition-all duration-400 cursor-pointer shadow-lg active:scale-98 disabled:opacity-60 disabled:cursor-not-allowed ${success ? 'bg-[#059669] shadow-emerald-600/20' : 'bg-gradient-to-r from-[#1883FF] to-[#6D28D9] shadow-[#6D28D9]/20 hover:opacity-90 hover:-translate-y-0.5'}`}
        >
          {saving ? <Loader2 className="animate-spin" size={20} /> : success ? <CheckCircle size={20} /> : <Save size={20} />}
          {saving ? 'Salvando...' : success ? 'Salvo!' : 'Salvar Alterações'}
        </button>

        <div className="p-4 bg-[#f0fdf4] rounded-2xl border border-[#dcfce7] flex items-center gap-3 text-[#166534] text-xs font-semibold leading-relaxed">
          <ShieldCheck size={20} className="text-emerald-600 shrink-0" />
          <p className="m-0">Seus dados estão protegidos pela criptografia de ponta a ponta da <strong>Agenda Sincro</strong>.</p>
        </div>
      </form>
    </div>
  );
};

export default DadosEstabelecimentoTab;
